import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, RotateCcw, Calendar, User, Tag, MapPin, Settings, Plus, Check, Trash, Download, Filter, List, CalendarCog, Copy, Clipboard } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatViewDate } from '@/utils/event-helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimelineView from '@/components/agenda/TimelineView';
import ModernEventFilters from '@/components/agenda/ModernEventFilters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEventsCopy } from '@/hooks/useEventsCopy';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import PasteConfirmationModal from '@/components/agenda/PasteConfirmationModal';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface AgendaSidebarProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date | undefined) => void;
  isExpanded: boolean;
  onToggle: () => void;
  onToggleView?: (view: string) => void;
  onPrevNext?: (direction: 'prev' | 'next') => void;
  onToday?: () => void;
  currentView: string;
  onNewLesson?: () => void;
  onMarkCompleted?: () => void;
  onDeleteEvents?: () => void;
  onExportEvents?: () => void;
  onCopyEvents?: () => void;
  showNewLessonDialog?: boolean;
  setShowNewLessonDialog?: (show: boolean) => void;
  timezone?: string;
  onFiltersChange?: (filters: any) => void;
  events?: any[];
  firstDay?: number;
  onRefreshEvents?: () => void;
}

const AgendaSidebar: React.FC<AgendaSidebarProps> = ({
  selectedDate,
  onDateSelect,
  onToggleView,
  onPrevNext,
  onToday,
  currentView,
  onNewLesson,
  onMarkCompleted,
  onDeleteEvents,
  onExportEvents,
  onCopyEvents,
  onFiltersChange,
  events = [],
  firstDay = 1,
  onRefreshEvents
}) => {
  const { t, locale } = useLanguage();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { token, user } = useAuth();
  const { copiedEvents, sourceView, sourceDate, sourceRange, hasCopied, clearCopiedEvents } = useEventsCopy();
  const [isTodayButtonVisible, setIsTodayButtonVisible] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [showPasteConfirmation, setShowPasteConfirmation] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // État pour gérer la visibilité des filtres

  const formattedDate = formatViewDate(selectedDate || new Date(), currentView, locale || 'en', firstDay);
  const weekStartsOn = (firstDay === 0 || firstDay === 1) ? (firstDay as 0 | 1) : 1;
  const defaultTabValue = isMobile ? "" : "calendar";

  const targetRange = useMemo(() => {
    if (selectedDate && currentView === 'timeGridWeek') {
      const start = new Date(selectedDate);
      const day = start.getDay();
      const diff = (day < firstDay ? 7 : 0) + day - firstDay;
      start.setDate(start.getDate() - diff);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start, end };
    }
    return undefined;
  }, [selectedDate, currentView, firstDay]);

  const isSameTimeframe = useMemo(() => {
    if (!hasCopied) return false;
    if (currentView === 'timeGridDay' && sourceDate && selectedDate) {
      return format(sourceDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
    }
    if (currentView === 'timeGridWeek' && sourceRange && targetRange) {
      return (
        format(sourceRange.start, 'yyyy-MM-dd') === format(targetRange.start, 'yyyy-MM-dd') &&
        format(sourceRange.end, 'yyyy-MM-dd') === format(targetRange.end, 'yyyy-MM-dd')
      );
    }
    return false;
  }, [hasCopied, currentView, sourceDate, selectedDate, sourceRange, targetRange]);

  const canPaste = hasCopied && (currentView === 'timeGridWeek' || currentView === 'timeGridDay') && !isSameTimeframe;

  const toggleTodayButtonVisibility = () => {
    setIsTodayButtonVisible((prev) => !prev);
  };

  const handlePasteClick = () => {
    if (!copiedEvents.length) return;
    setShowPasteConfirmation(true);
  };

  const handlePasteSuccess = () => {
    toast({
      title: t('agenda.eventsPasted'),
      description: `${copiedEvents.length} ${t('agenda.eventsCopiedSuccessfully')}`,
    });
    clearCopiedEvents();
    if (onRefreshEvents) {
      onRefreshEvents();
    }
  };

  const handlePasteError = (errorMessage: string) => {
    toast({
      title: t('agenda.errorPastingEvents'),
      description: errorMessage || t('agenda.pleaseTryAgain'),
      variant: "destructive"
    });
  };

  /*const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      console.log('Date selected:', date);
      onDateSelect(date);
    }
  };*/


  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    const userTimezone = user.timezone || 'UTC';

    const formattedDate = formatInTimeZone(
      date,
      userTimezone,
      "yyyy-MM-dd'T'HH:mm:ssXXX"
    );
    onDateSelect(new Date(formattedDate));
  };

  return (
    <div className="h-full flex flex-col bg-white border border-gray-200 shadow-sm rounded-md overflow-hidden">
      <ScrollArea className="h-full">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between w-full">
            <Button variant="outline" size="sm" onClick={() => onPrevNext && onPrevNext('prev')} className="rounded-md h-8 w-8 p-0 mx-2">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium flex-grow m-0">
              <Button variant="outline" size="sm" className="rounded-md w-full" onClick={toggleTodayButtonVisibility}>
                {formattedDate}
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => onPrevNext && onPrevNext('next')} className="rounded-md h-8 w-8 p-0 mx-2">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {isTodayButtonVisible && (
            <Button
              variant="outline"
              className="flex items-center justify-center w-full"
              onClick={onToday}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {t('agenda.today')}
            </Button>
          )}
          <div className="flex space-x-2">
            <div className="flex-1">
              <Select
                onValueChange={onToggleView}
                defaultValue="timeGridWeek"
                value={currentView}
              >
                <SelectTrigger className={`w-full flex items-center justify-between ${isMobile ? 'text-sm' : ''}`}>
                  <div className="flex items-center">
                    <List className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={t('agenda.viewType')} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dayGridMonth">{t('agenda.viewMonth')}</SelectItem>
                  <SelectItem value="multiMonthYear">{t('agenda.viewYear')}</SelectItem>
                  <SelectItem value="timeGridWeek">{t('agenda.viewWeek')}</SelectItem>
                  <SelectItem value="timeGridDay">{t('agenda.viewDay')}</SelectItem>
                  <SelectItem value="listWeek">{t('agenda.viewList')}</SelectItem>
                  {currentView === 'listYear' && (
                    <SelectItem value="listYear">{t('agenda.viewListYear')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {isMobile && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            )}
            {user.account_type !== "Student" && user.account_type !== "Parent" && (
              <div className="flex-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        <CalendarCog size={16} className="mr-2" />
                        {t('agenda.manageLessons')}
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={onMarkCompleted} className="flex items-center cursor-pointer">
                      <Check className="mr-2 h-4 w-4" />
                      <span>{t('agenda.markAsCompleted')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onCopyEvents} className="flex items-center cursor-pointer">
                      <Copy className="mr-2 h-4 w-4" />
                      <span>{t('agenda.copyAll')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onExportEvents} className="flex items-center cursor-pointer">
                      <Download className="mr-2 h-4 w-4" />
                      <span>{t('agenda.exportLessons')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDeleteEvents} className="flex items-center cursor-pointer text-red-500 hover:text-red-600">
                      <Trash className="mr-2 h-4 w-4" />
                      <span>{t('agenda.deleteEvents')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
            <Button
              id="agenda-new-lesson-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center"
              onClick={onNewLesson}
            >
              <Plus size={16} className="mr-2" />
              {t('agenda.newLesson')} 
            </Button>
          {canPaste && (
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
              onClick={handlePasteClick}
            >
              <Clipboard size={16} className="mr-2" />
              {t('agenda.pasteNow')}
            </Button>
          )}
          {isMobile && showFilters && (
            <div className="mt-2">
              <ModernEventFilters
                selectedDate={selectedDate}
                onFiltersChange={onFiltersChange}
                events={events}
                filterType="all"
              />
            </div>
          )}
          {!isMobile && (
            <Tabs defaultValue={defaultTabValue} className="w-full">
              {user.account_type !== "Student" && user.account_type !== "Parent" && (
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="calendar" className="flex items-center justify-center">
                    <Calendar className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="filters" className="flex items-center justify-center">
                    <Filter className="h-4 w-4" />
                  </TabsTrigger>
                </TabsList>
              )}
              <TabsContent value="calendar" className="mt-4">
                <div className="w-full flex items-center justify-center">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate || undefined}
                    onSelect={handleDateSelect}
                    className="rounded-md"
                    weekStartsOn={weekStartsOn}
                  />
                </div>
              </TabsContent>
              <TabsContent value="filters" className="mt-2">
                <ModernEventFilters
                  selectedDate={selectedDate}
                  onFiltersChange={onFiltersChange}
                  events={events}
                  filterType="all"
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </ScrollArea>
      <PasteConfirmationModal
        open={showPasteConfirmation}
        onOpenChange={setShowPasteConfirmation}
        eventIds={copiedEvents}
        targetView={currentView}
        targetDate={selectedDate || new Date()}
        targetRange={targetRange}
        sourceDate={sourceDate}
        sourceRange={sourceRange}
        token={token}
        onSuccess={handlePasteSuccess}
        onError={handlePasteError}
      />
    </div>
  );
};

export default AgendaSidebar;
